import React, { useState, useEffect, useRef } from 'react';
import IconThumbnail from '../../components/shared/IconThumbnail';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, Edit, Trash2, Search, Loader2, Grid3X3,
  Square, Star, Image, Smile, Upload, FileUp, Globe, EyeOff
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';
import CategorySelectWithAdd from '../../components/admin/CategorySelectWithAdd';
import { isGenericName } from '../../utils/displayName';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_BY_KEY = { shape: Square, icon: Star, sticker: Smile, image: Image };

const AdminElements = () => {
  const { toast } = useToast();
  const [elements, setElements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [elementTypes, setElementTypes] = useState([
    { value: 'shape', label: 'Shapes', icon: Square },
    { value: 'icon', label: 'Icons', icon: Star },
    { value: 'sticker', label: 'Stickers', icon: Smile },
    { value: 'image', label: 'Images', icon: Image },
  ]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'shape',
    category_id: '',
    thumbnail: '',
    svg_content: '',
    emoji: '',
    image_url: '',
    default_width: 100,
    default_height: 100,
    default_fill: '#6366f1',
    default_stroke: '',
    is_premium: false,
    tags: []
  });
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageUploadRef = useRef(null);
  const thumbnailUploadRef = useRef(null);
  const svgUploadRef = useRef(null);

  // Bulk upload dialog state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkType, setBulkType] = useState('shape');
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkWidth, setBulkWidth] = useState(100);
  const [bulkHeight, setBulkHeight] = useState(100);
  const [bulkPremium, setBulkPremium] = useState(false);
  const [bulkPublish, setBulkPublish] = useState(true);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFilesRef = useRef(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Fetch elements
      const elementsRes = await fetch(`${API_URL}/api/admin/content/elements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (elementsRes.ok) {
        const data = await elementsRes.json();
        setElements(data);
      }

      // Fetch element categories
      const categoriesRes = await fetch(`${API_URL}/api/admin/content/categories?type=element`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data);
      }

      // Fetch element types
      const typesRes = await fetch(`${API_URL}/api/admin/content/element-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (typesRes.ok) {
        const data = await typesRes.json();
        if (Array.isArray(data) && data.length > 0) {
          setElementTypes(data.map(t => ({
            value: t.key,
            label: t.name,
            icon: ICON_BY_KEY[t.key] || Grid3X3,
          })));
        }
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (element = null) => {
    if (element) {
      setEditingElement(element);
      setFormData({
        name: element.name,
        type: element.type,
        category_id: element.category_id,
        thumbnail: element.thumbnail || '',
        svg_content: element.svg_content || '',
        emoji: element.emoji || '',
        image_url: element.image_url || '',
        default_width: element.default_width || 100,
        default_height: element.default_height || 100,
        default_fill: element.default_fill || '#6366f1',
        default_stroke: element.default_stroke || '',
        is_premium: element.is_premium || false,
        tags: element.tags || []
      });
      setTagsInput(element.tags?.join(', ') || '');
    } else {
      setEditingElement(null);
      setFormData({
        name: '',
        type: 'shape',
        category_id: categories[0]?.id || '',
        thumbnail: '',
        svg_content: '',
        emoji: '',
        image_url: '',
        default_width: 100,
        default_height: 100,
        default_fill: '#6366f1',
        default_stroke: '',
        is_premium: false,
        tags: []
      });
      setTagsInput('');
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Element name is required', variant: 'destructive' });
      return;
    }
    if (!formData.category_id) {
      toast({ title: 'Error', description: 'Please select a category', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingElement 
        ? `${API_URL}/api/admin/content/elements/${editingElement.id}`
        : `${API_URL}/api/admin/content/elements`;
      
      // Process tags
      const tags = tagsInput.split(',').map(t => t.trim()).filter(t => t);
      
      const response = await fetch(url, {
        method: editingElement ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...formData, tags })
      });

      if (response.ok) {
        toast({ title: 'Success', description: `Element ${editingElement ? 'updated' : 'created'} successfully` });
        setDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save element');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async (element) => {
    const action = element.is_published ? 'unpublish' : 'publish';
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/content/elements/${element.id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const updated = await response.json();
        setElements(prev => prev.map(e => e.id === updated.id ? updated : e));
        toast({ title: 'Success', description: `Element ${action}ed successfully` });
      } else {
        throw new Error(`Failed to ${action} element`);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (element) => {
    if (!window.confirm(`Are you sure you want to delete "${element.name}"?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/content/elements/${element.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Element deleted successfully' });
        fetchData();
      } else {
        throw new Error('Failed to delete element');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const uploadFileToAsset = async (file) => {
    const token = localStorage.getItem('admin_token');
    if (!token || !API_URL) {
      toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
      return null;
    }
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/admin/content/assets/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Upload failed');
    }
    const asset = await res.json();
    const fullUrl = asset.url?.startsWith('http') ? asset.url : `${API_URL}${asset.url}`;
    return fullUrl;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Error', description: 'Use PNG, JPG, GIF, WebP or SVG', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFileToAsset(file);
      if (url) {
        setFormData(prev => ({ ...prev, image_url: url, thumbnail: prev.thumbnail || url }));
        toast({ title: 'Uploaded', description: 'Image URL set' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Error', description: 'Use PNG, JPG, GIF, WebP or SVG', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFileToAsset(file);
      if (url) {
        setFormData(prev => ({ ...prev, thumbnail: url }));
        toast({ title: 'Uploaded', description: 'Thumbnail URL set' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSvgFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/svg+xml' && !file.name.toLowerCase().endsWith('.svg')) {
      toast({ title: 'Error', description: 'Please select an SVG file', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === 'string') {
        setFormData(prev => ({ ...prev, svg_content: text }));
        toast({ title: 'Loaded', description: 'SVG content set from file' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    const accepted = files.filter(f =>
      allowed.includes(f.type) || f.name.toLowerCase().endsWith('.svg')
    );
    setBulkFiles(accepted);
    e.target.value = '';
  };

  const handleBulkSubmit = async () => {
    if (!bulkCategoryId) {
      toast({ title: 'Error', description: 'Please select a category', variant: 'destructive' });
      return;
    }
    if (bulkFiles.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one file', variant: 'destructive' });
      return;
    }
    setBulkUploading(true);
    setBulkProgress(0);
    setBulkResult(null);

    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    bulkFiles.forEach(f => fd.append('files', f));
    fd.append('type', bulkType);
    fd.append('category_id', bulkCategoryId);
    fd.append('default_width', String(bulkWidth));
    fd.append('default_height', String(bulkHeight));
    fd.append('is_premium', bulkPremium ? 'true' : 'false');
    fd.append('is_published', bulkPublish ? 'true' : 'false');

    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/admin/content/elements/bulk`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setBulkProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Invalid JSON response')); }
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });

      setBulkResult(result);
      const createdCount = (result.created || []).length;
      const errorCount = (result.errors || []).length;
      toast({
        title: errorCount === 0 ? 'Success' : 'Partial success',
        description: `Created ${createdCount} elements${errorCount ? `, ${errorCount} failed` : ''}`,
        variant: errorCount === 0 ? 'default' : 'destructive',
      });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBulkUploading(false);
    }
  };

  const filteredElements = elements.filter(elem => {
    const matchesSearch = elem.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         elem.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || elem.type === filterType;
    const matchesCategory = filterCategory === 'all' || elem.category_id === filterCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || 'Unknown';
  };

  const renderElementPreview = (element) => {
    if (element.emoji) {
      return <span className="text-4xl">{element.emoji}</span>;
    }
    if (element.svg_content) {
      return <IconThumbnail svg={element.svg_content} alt={element.name} size={48} innerScale={0.85} />;
    }
    if (element.image_url || element.thumbnail) {
      return (
        <img 
          src={element.image_url || element.thumbnail} 
          alt={element.name}
          className="w-12 h-12 object-contain"
        />
      );
    }
    return <Grid3X3 className="w-8 h-8 text-gray-400" />;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Elements</h1>
            <p className="text-gray-500 mt-1">Manage shapes, icons, stickers, and images</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBulkOpen(true);
                setBulkFiles([]);
                setBulkResult(null);
                setBulkProgress(0);
                setBulkCategoryId(categories[0]?.id || '');
              }}
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Element
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search elements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {elementTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Elements Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredElements.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Grid3X3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No elements found</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add your first element
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredElements.map(element => (
              <Card key={element.id} className="group hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center mb-3 relative">
                    {renderElementPreview(element)}
                    {element.is_premium && (
                      <Badge className="absolute top-2 right-2 bg-amber-500">Pro</Badge>
                    )}
                    {element.is_published && (
                      <Badge className="absolute top-2 left-2 bg-emerald-500 px-1.5 py-0.5 text-[10px]">Live</Badge>
                    )}
                  </div>
                  {!isGenericName(element.name) && (
                    <p className="font-medium text-gray-900 truncate text-sm">{element.name}</p>
                  )}
                  <p className={`text-xs text-gray-500 truncate ${isGenericName(element.name) ? 'mt-1' : ''}`}>{getCategoryName(element.category_id)}</p>
                  <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`flex-1 h-8 ${element.is_published ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-gray-500 hover:bg-gray-100'}`}
                      onClick={() => handlePublishToggle(element)}
                      title={element.is_published ? 'Unpublish' : 'Publish to editor'}
                    >
                      {element.is_published ? <EyeOff className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-8"
                      onClick={() => handleOpenDialog(element)}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(element)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingElement ? 'Edit Element' : 'Add New Element'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Element Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Rectangle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {elementTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <CategorySelectWithAdd
                label="Category"
                type="element"
                required
                categories={categories}
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                onCategoryCreated={(cat) => setCategories(prev => [...prev, cat])}
                placeholder="Select category"
              />

              <Tabs defaultValue="content" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="styling">Styling</TabsTrigger>
                  <TabsTrigger value="meta">Meta</TabsTrigger>
                </TabsList>
                
                <TabsContent value="content" className="space-y-4 mt-4">
                  {formData.type === 'sticker' && (
                    <div className="space-y-2">
                      <Label>Emoji</Label>
                      <Input
                        value={formData.emoji}
                        onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                        placeholder="e.g., 😀"
                      />
                    </div>
                  )}
                  
                  {(formData.type === 'shape' || formData.type === 'icon') && (
                    <div className="space-y-2">
                      <Label>SVG Content</Label>
                      <div className="flex gap-2">
                        <Textarea
                          value={formData.svg_content}
                          onChange={(e) => setFormData({ ...formData, svg_content: e.target.value })}
                          placeholder="<svg>...</svg>"
                          rows={5}
                          className="font-mono text-sm flex-1"
                        />
                        <div className="flex flex-col gap-1">
                          <input
                            ref={svgUploadRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="hidden"
                            onChange={handleSvgFileUpload}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => svgUploadRef.current?.click()}
                            className="shrink-0"
                            title="Upload SVG file"
                          >
                            <FileUp className="w-4 h-4 mr-1" />
                            Upload SVG
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Paste SVG markup or upload an .svg file</p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Image URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://... or upload file"
                        className="flex-1"
                      />
                      <input
                        ref={imageUploadRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        onClick={() => imageUploadRef.current?.click()}
                        className="shrink-0"
                        title="Upload PNG, JPG, GIF, WebP or SVG"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Upload
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Enter URL or upload PNG, JPG, GIF, WebP, SVG</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Thumbnail URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.thumbnail}
                        onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                        placeholder="https://... or upload file"
                        className="flex-1"
                      />
                      <input
                        ref={thumbnailUploadRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={handleThumbnailUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        onClick={() => thumbnailUploadRef.current?.click()}
                        className="shrink-0"
                        title="Upload thumbnail"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        Upload
                      </Button>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="styling" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Default Width</Label>
                      <Input
                        type="number"
                        value={formData.default_width}
                        onChange={(e) => setFormData({ ...formData, default_width: parseInt(e.target.value) || 100 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Default Height</Label>
                      <Input
                        type="number"
                        value={formData.default_height}
                        onChange={(e) => setFormData({ ...formData, default_height: parseInt(e.target.value) || 100 })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Default Fill Color</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.default_fill}
                          onChange={(e) => setFormData({ ...formData, default_fill: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={formData.default_fill}
                          onChange={(e) => setFormData({ ...formData, default_fill: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Default Stroke Color</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.default_stroke || '#000000'}
                          onChange={(e) => setFormData({ ...formData, default_stroke: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={formData.default_stroke}
                          onChange={(e) => setFormData({ ...formData, default_stroke: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="meta" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Tags (comma separated)</Label>
                    <Input
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      placeholder="e.g., basic, geometric, modern"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_premium"
                      checked={formData.is_premium}
                      onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_premium">Premium Element (Pro users only)</Label>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingElement ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkOpen} onOpenChange={(o) => { if (!bulkUploading) setBulkOpen(o); }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bulk Upload Elements</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={bulkType} onValueChange={setBulkType} disabled={bulkUploading}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {elementTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <CategorySelectWithAdd
                  label="Category *"
                  type="element"
                  required
                  categories={categories}
                  value={bulkCategoryId}
                  onValueChange={setBulkCategoryId}
                  onCategoryCreated={(cat) => setCategories(prev => [...prev, cat])}
                  placeholder="Select category"
                />
              </div>

              <div className="space-y-2">
                <Label>Files *</Label>
                <input
                  ref={bulkFilesRef}
                  type="file"
                  multiple
                  accept=".svg,.png,.jpg,.jpeg,.gif,.webp,image/svg+xml,image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={handleBulkFilesSelected}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={bulkUploading}
                  onClick={() => bulkFilesRef.current?.click()}
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  {bulkFiles.length > 0 ? `${bulkFiles.length} files selected` : 'Choose files'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Drop SVG / PNG / JPG / GIF / WebP. Each file becomes one element; the filename (without extension) is used as the element name.
                </p>
                {bulkFiles.length > 0 && (
                  <div className="grid grid-cols-8 gap-2 mt-2 max-h-40 overflow-y-auto p-2 border rounded">
                    {bulkFiles.slice(0, 64).map((f, i) => (
                      <div key={i} className="aspect-square bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-500 truncate p-1" title={f.name}>
                        {f.name.replace(/\.[^.]+$/, '')}
                      </div>
                    ))}
                    {bulkFiles.length > 64 && (
                      <div className="aspect-square bg-gray-50 rounded flex items-center justify-center text-[10px] text-gray-400">
                        +{bulkFiles.length - 64} more
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Width</Label>
                  <Input type="number" value={bulkWidth} onChange={(e) => setBulkWidth(parseInt(e.target.value) || 100)} disabled={bulkUploading} />
                </div>
                <div className="space-y-2">
                  <Label>Default Height</Label>
                  <Input type="number" value={bulkHeight} onChange={(e) => setBulkHeight(parseInt(e.target.value) || 100)} disabled={bulkUploading} />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={bulkPremium} onChange={(e) => setBulkPremium(e.target.checked)} disabled={bulkUploading} className="rounded" />
                  Premium (Pro users only)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={bulkPublish} onChange={(e) => setBulkPublish(e.target.checked)} disabled={bulkUploading} className="rounded" />
                  Publish immediately
                </label>
              </div>

              {bulkUploading && (
                <div className="space-y-1">
                  <div className="text-xs text-gray-600">Uploading… {bulkProgress}%</div>
                  <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                    <div className="h-full bg-purple-600 transition-all" style={{ width: `${bulkProgress}%` }} />
                  </div>
                </div>
              )}

              {bulkResult && (bulkResult.errors || []).length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-sm">
                  <div className="font-medium text-red-700 mb-1">Errors ({bulkResult.errors.length}):</div>
                  <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                    {bulkResult.errors.map((e, i) => (
                      <li key={i}>{e.filename}: {e.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkUploading}>
                {bulkResult ? 'Close' : 'Cancel'}
              </Button>
              <Button
                onClick={handleBulkSubmit}
                disabled={bulkUploading || bulkFiles.length === 0 || !bulkCategoryId}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {bulkUploading ? 'Uploading…' : `Upload ${bulkFiles.length || ''} files`.trim()}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminElements;
